import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';
import PayrollTabs from '../../components/PayrollTabs';
import PayrollLoansTab from './PayrollLoansTab';
import PayrollLeaveTab from './PayrollLeaveTab';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const initialForm = {
  name: '', phone: '', email: '', employee_code: '', designation: '', branch: '', payroll_department: '',
  doj_pf: '', uan_no: '', pf_number: '',
  bank_name: '', bank_account_number: '', bank_ifsc: '',
  basic_da: '', hra: '', conveyance: '', medical_allowance: '', special_allowance: '', other_allowance: '0',
  income_tax: '0',
  month_days_standard: '26', pf_applicable: true, esic_applicable: false, pt_applicable: true,
};

// This module's Employee Master is independent of the app's People page
// (see app/models.py's HR PAYROLL MODULE section docstring) - a payroll
// profile is its own person record with its own name, not a pick from an
// existing Employee. That's deliberate: many real payroll rows (the
// August FACTORY/HO sheets included) have no Komal OS login at all.
//
// One form doubles as Add and Edit (2026-09-12) - a separate compact
// inline-row editor used to exist here but only covered a handful of
// fields, silently leaving phone/bank details/UAN un-editable after
// import. Editing now opens this same full form pre-filled, so every
// field the master holds is reachable from one place.
const PayrollEmployeesPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profiles'); // 'profiles' | 'loans' | 'leave'
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = adding a new profile
  const [form, setForm] = useState(initialForm);
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    payrollService.getProfiles()
      .then((res) => setProfiles(res.data.profiles || []))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load payroll employees', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const openAddForm = () => {
    setEditingId(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEditForm = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, phone: p.phone || '', email: p.email || '', employee_code: p.employee_code || '',
      designation: p.designation || '', branch: p.branch || '', payroll_department: p.payroll_department || '',
      doj_pf: p.doj_pf || '', uan_no: p.uan_no || '', pf_number: p.pf_number || '',
      bank_name: p.bank_name || '', bank_account_number: p.bank_account_number || '', bank_ifsc: p.bank_ifsc || '',
      basic_da: p.basic_da, hra: p.hra, conveyance: p.conveyance,
      medical_allowance: p.medical_allowance, special_allowance: p.special_allowance,
      other_allowance: p.other_allowance, income_tax: p.income_tax,
      month_days_standard: p.month_days_standard,
      pf_applicable: p.pf_applicable, esic_applicable: p.esic_applicable, pt_applicable: p.pt_applicable,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      doj_pf: form.doj_pf || null,
      basic_da: Number(form.basic_da) || 0,
      hra: Number(form.hra) || 0,
      conveyance: Number(form.conveyance) || 0,
      medical_allowance: Number(form.medical_allowance) || 0,
      special_allowance: Number(form.special_allowance) || 0,
      other_allowance: Number(form.other_allowance) || 0,
      income_tax: Number(form.income_tax) || 0,
      month_days_standard: Number(form.month_days_standard) || 26,
    };
    try {
      if (editingId) {
        await payrollService.updateProfile(editingId, payload);
        showToast('Payroll profile updated', 'success');
      } else {
        await payrollService.createProfile(payload);
        showToast('Payroll profile created', 'success');
      }
      closeForm();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save payroll profile', 'error');
    }
    setSubmitting(false);
  };

  const handleDeactivate = async (p) => {
    if (!window.confirm(`Deactivate ${p.name}'s payroll profile? Past payslips stay on record.`)) return;
    try {
      await payrollService.deactivateProfile(p.id);
      showToast('Payroll profile deactivated', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to deactivate profile', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <PayrollTabs />

        <div className="flex gap-1 mb-4">
          {[['profiles', 'Employee Profiles'], ['loans', 'Loans'], ['leave', 'Leave']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`text-sm px-4 py-1.5 rounded font-semibold ${tab === key ? 'bg-white text-komal-blue' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'loans' ? (
          <PayrollLoansTab profiles={profiles} />
        ) : tab === 'leave' ? (
          <PayrollLeaveTab profiles={profiles} />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Employee Master ({profiles.length})</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/payroll/import-emails')}
                  className="px-4 py-2 text-sm rounded border border-white/40 text-white hover:bg-white/10 font-semibold"
                >
                  Bulk Import Emails
                </button>
                <button
                  onClick={() => (showForm ? closeForm() : openAddForm())}
                  className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold"
                >
                  {showForm ? 'Cancel' : '+ Add Payroll Profile'}
                </button>
              </div>
            </div>

            {showForm && (
              <form onSubmit={handleSubmit} className="glass-card p-6 mb-6 space-y-4">
                <h3 className="font-semibold text-gray-900">{editingId ? 'Edit Payroll Profile' : 'Add Payroll Profile'}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input required value={form.name} onChange={handleChange('name')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input value={form.phone} onChange={handleChange('phone')} placeholder="+91 98192 11693" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={form.email} onChange={handleChange('email')} placeholder="for payslip delivery" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
                    <input value={form.employee_code} onChange={handleChange('employee_code')} placeholder="KW000406" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                    <input value={form.designation} onChange={handleChange('designation')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                    <input value={form.branch} onChange={handleChange('branch')} placeholder="FACTORY / HO" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department (payroll label)</label>
                    <input value={form.payroll_department} onChange={handleChange('payroll_department')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Joining</label>
                    <input type="date" value={form.doj_pf} onChange={handleChange('doj_pf')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UAN</label>
                    <input value={form.uan_no} onChange={handleChange('uan_no')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Standard Month Days</label>
                    <input type="number" value={form.month_days_standard} onChange={handleChange('month_days_standard')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>

                  <div className="col-span-3 border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Basic + DA (₹)</label>
                      <input required type="number" value={form.basic_da} onChange={handleChange('basic_da')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">HRA (₹)</label>
                      <input type="number" value={form.hra} onChange={handleChange('hra')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Conveyance (₹)</label>
                      <input type="number" value={form.conveyance} onChange={handleChange('conveyance')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Medical Allowance (₹)</label>
                      <input type="number" value={form.medical_allowance} onChange={handleChange('medical_allowance')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Allowance (₹)</label>
                      <input type="number" value={form.special_allowance} onChange={handleChange('special_allowance')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other Allowance (₹)</label>
                      <input type="number" value={form.other_allowance} onChange={handleChange('other_allowance')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                  </div>

                  <div className="col-span-3 border-t border-gray-100 pt-4">
                    <div className="max-w-xs">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax / TDS (₹) <span className="text-gray-400 font-normal">— deduction, not allowance</span></label>
                      <input type="number" value={form.income_tax} onChange={handleChange('income_tax')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                  </div>

                  <div className="col-span-3 border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                      <input value={form.bank_name} onChange={handleChange('bank_name')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number</label>
                      <input value={form.bank_account_number} onChange={handleChange('bank_account_number')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IFSC</label>
                      <input value={form.bank_ifsc} onChange={handleChange('bank_ifsc')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                  </div>

                  <div className="col-span-3 flex gap-6 pt-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={form.pf_applicable} onChange={handleChange('pf_applicable')} /> PF applicable
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={form.esic_applicable} onChange={handleChange('esic_applicable')} /> ESIC applicable
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={form.pt_applicable} onChange={handleChange('pt_applicable')} /> PT applicable
                    </label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="submit" disabled={submitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                    {submitting ? 'Saving...' : editingId ? 'Save Changes' : 'Save Payroll Profile'}
                  </button>
                  <button type="button" onClick={closeForm} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="glass-card">
              {loading ? (
                <p className="text-gray-500 text-sm p-5">Loading...</p>
              ) : (
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Phone</th>
                        <th className="px-4 py-2 font-medium">Email</th>
                        <th className="px-4 py-2 font-medium">Designation</th>
                        <th className="px-4 py-2 font-medium">Branch</th>
                        <th className="px-4 py-2 font-medium">Bank</th>
                        <th className="px-4 py-2 font-medium text-right">Gross/Month</th>
                        <th className="px-4 py-2 font-medium text-right">Income Tax</th>
                        <th className="px-4 py-2 font-medium">PF / ESIC / PT</th>
                        <th className="px-4 py-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profiles.map((p) => (
                        <tr key={p.id} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-3 text-gray-500">{p.employee_code || '—'}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                          <td className="px-4 py-3 text-gray-500">{p.phone || '—'}</td>
                          <td className="px-4 py-3 text-gray-500">{p.email || '—'}</td>
                          <td className="px-4 py-3">{p.designation || '—'}</td>
                          <td className="px-4 py-3">{p.branch || '—'}</td>
                          <td className="px-4 py-3 text-gray-500">
                            {p.bank_name ? `${p.bank_name} ${p.bank_account_number ? `(•••${String(p.bank_account_number).slice(-4)})` : ''}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">{money(p.gross_salary)}</td>
                          <td className="px-4 py-3 text-right text-gray-500">{p.income_tax ? money(p.income_tax) : '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {p.pf_applicable ? 'PF' : '—'} / {p.esic_applicable ? 'ESIC' : '—'} / {p.pt_applicable ? 'PT' : '—'}
                          </td>
                          <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                            <button onClick={() => openEditForm(p)} className="text-komal-blue hover:underline text-xs font-semibold">Edit</button>
                            <button onClick={() => handleDeactivate(p)} className="text-red-600 hover:underline text-xs font-semibold">Deactivate</button>
                          </td>
                        </tr>
                      ))}
                      {profiles.length === 0 && (
                        <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">No payroll profiles yet - add one above, or use "Load Old Salary Sheet" on the Payroll Runs page.</td></tr>
                      )}
                    </tbody>
                  </table>
                </ScrollX>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollEmployeesPage;
